/**
 * Booking Intake Funnel: small Dashboard widget.
 *
 * Shows status counts + per-source breakdown for the selected period.
 * Lightweight by design: no charts, no revenue attribution, no exports.
 *
 * Hidden gracefully if the user doesn't have `booking_intake` permission
 * (the API will 403 and we just won't render).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Inbox, ArrowRight, MessageSquareText, MessageCircle,
  Phone, Footprints, FileSpreadsheet, FileText, Globe, Smartphone,
} from "lucide-react";

const PERIOD_LABEL = {
  today: "Today",
  last_7_days: "Last 7 days",
  this_month: "This month",
  all_time: "All time",
};

const SOURCE_LABEL = {
  messenger: "Messenger",
  whatsapp: "WhatsApp",
  viber: "Viber",
  sms: "SMS",
  facebook: "Facebook",
  phone: "Phone",
  walk_in: "Walk-in",
  quick_note: "Quick note",
  google_sheet: "Google Sheets",
  csv: "CSV import",
  website: "Website",
  other: "Other",
};

const SOURCE_ICON = {
  messenger: MessageSquareText,
  whatsapp: MessageCircle,
  viber: MessageCircle,
  sms: Smartphone,
  facebook: MessageSquareText,
  phone: Phone,
  walk_in: Footprints,
  quick_note: FileText,
  google_sheet: FileSpreadsheet,
  csv: FileSpreadsheet,
  website: Globe,
  other: Inbox,
};

export default function BookingIntakeFunnel() {
  const [period, setPeriod] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/booking-intake/funnel?period=${period}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (err?.response?.status === 403) setHidden(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  if (hidden) return null;

  const c = data?.counts || {};
  const cards = [
    { label: "Drafts created",   value: c.drafts_created ?? 0,
      testId: "fn-drafts-created" },
    { label: "Staff approved",   value: c.staff_approved ?? 0,
      testId: "fn-staff-approved" },
    { label: "Link sent",        value: c.confirmation_sent ?? 0,
      testId: "fn-confirmation-sent" },
    { label: "Customer confirmed", value: c.customer_confirmed ?? 0,
      testId: "fn-customer-confirmed", success: true },
    { label: "Change requested", value: c.customer_requested_change ?? 0,
      testId: "fn-customer-change", warn: true },
    { label: "Discarded",        value: c.discarded ?? 0,
      testId: "fn-discarded", muted: true },
  ];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5"
              data-testid="dashboard-intake-funnel">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl font-semibold">
            Booking Intake Funnel
          </h2>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">
            See how notes, pasted chats, and imports become customer-confirmed bookings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="intake-funnel-period"
            className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm"
          >
            {Object.entries(PERIOD_LABEL).map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
          <Link to="/app/booking-intake"
                 data-testid="intake-funnel-cta"
                 className="hidden sm:inline-flex text-sm text-[#1B4332] font-medium hover:underline items-center gap-1">
            Open Intake Hub <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-slate-500">Loading funnel…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {cards.map((card) => (
              <div key={card.label}
                    data-testid={card.testId}
                    className={`rounded-lg p-3 border ${
                      card.success ? "border-emerald-200 bg-emerald-50" :
                      card.warn    ? "border-rose-200 bg-rose-50" :
                      card.muted   ? "border-slate-200 bg-slate-50 text-slate-500" :
                                      "border-slate-200 bg-white"
                    }`}>
                <div className="text-xs uppercase tracking-[0.14em] font-semibold opacity-75">
                  {card.label}
                </div>
                <div className="text-2xl font-display font-bold mt-1">
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {(data?.by_source?.length || 0) > 0 ? (
            <div data-testid="intake-funnel-sources" className="space-y-1.5">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                By source channel
              </div>
              <ul className="divide-y divide-slate-100">
                {data.by_source.map((row) => {
                  const Icon = SOURCE_ICON[row.source] || Inbox;
                  return (
                    <li key={row.source}
                        className="flex items-center justify-between py-1.5 text-sm gap-3">
                      <span className="inline-flex items-center gap-2 text-slate-800 min-w-0">
                        <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <span className="truncate">
                          {SOURCE_LABEL[row.source] || row.source}
                        </span>
                      </span>
                      <span className="text-slate-600 text-xs sm:text-sm whitespace-nowrap">
                        <strong>{row.drafts}</strong> draft{row.drafts === 1 ? "" : "s"}
                        <span className="text-slate-400"> → </span>
                        <strong>{row.approved}</strong> approved
                        <span className="text-slate-400"> → </span>
                        <strong className="text-emerald-700">{row.customer_confirmed}</strong> confirmed
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              No intake activity in this period yet -{" "}
              <Link to="/app/booking-intake"
                     className="text-[#1B4332] underline hover:no-underline">
                turn a Messenger chat or note into a draft
              </Link>{" "}to get started.
            </p>
          )}
        </>
      )}
    </section>
  );
}
